import * as ImagePicker from 'expo-image-picker';
import * as FileSystem from 'expo-file-system';
import { Platform } from 'react-native';

export type PickedProductVideo = {
  uri: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  base64: string;
};

export class ProductVideoValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProductVideoValidationError';
  }
}

export function isProductVideoValidationError(err: unknown): err is ProductVideoValidationError {
  return err instanceof ProductVideoValidationError;
}

const MAX_FILE_SIZE = 50 * 1024 * 1024;
const ALLOWED_MIMES = new Set(['video/mp4', 'video/webm', 'video/quicktime']);

function assertValid(mimeType: string, fileSize: number): void {
  if (!ALLOWED_MIMES.has(mimeType)) {
    throw new ProductVideoValidationError('Solo se admiten videos MP4 o WebM.');
  }
  if (fileSize > MAX_FILE_SIZE) {
    throw new ProductVideoValidationError('El video no puede superar los 50 MB.');
  }
}

async function readFileAsBase64(uri: string): Promise<string> {
  if (Platform.OS === 'web') {
    const resp = await fetch(uri);
    const blob = await resp.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          const commaIdx = result.indexOf(',');
          resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
        } else {
          reject(new Error('No se pudo procesar el video.'));
        }
      };
      reader.onerror = () => reject(new Error('Error al leer el video.'));
      reader.readAsDataURL(blob);
    });
  }
  return FileSystem.readAsStringAsync(uri, { encoding: 'base64' as const });
}

async function pickProductVideoNative(): Promise<PickedProductVideo | null> {
  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ImagePicker.MediaTypeOptions.Videos,
    quality: 1.0,
    base64: false,
    allowsEditing: false,
  });

  if (result.canceled || !result.assets?.[0]?.uri) return null;

  const asset = result.assets[0];
  const mimeType = asset.mimeType === 'video/quicktime' ? 'video/mp4' : asset.mimeType ?? 'video/mp4';
  const fileSize = asset.fileSize ?? 0;

  assertValid(mimeType, fileSize);

  const base64 = asset.base64 ?? (await readFileAsBase64(asset.uri));
  return {
    uri: asset.uri,
    fileName: asset.fileName ?? 'video.mp4',
    mimeType,
    fileSize,
    base64,
  };
}

function pickProductVideoWebInput(): Promise<PickedProductVideo | null> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/mp4,video/webm';
    input.onchange = async () => {
      try {
        const file = input.files?.[0];
        if (!file) return resolve(null);
        assertValid(file.type, file.size);
        const dataUrl = await new Promise<string>((res, rej) => {
          const reader = new FileReader();
          reader.onloadend = () => res(String(reader.result));
          reader.onerror = () => rej(new Error('Error al leer el video.'));
          reader.readAsDataURL(file);
        });
        const commaIdx = dataUrl.indexOf(',');
        resolve({
          uri: dataUrl,
          fileName: file.name,
          mimeType: file.type,
          fileSize: file.size,
          base64: commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl,
        });
      } catch (err) {
        reject(err);
      }
    };
    input.oncancel = () => resolve(null);
    input.click();
  });
}

/** Reel del producto — mp4/webm ≤50MB. El detalle de tienda lo muestra autoplay muted. */
export async function pickProductVideo(): Promise<PickedProductVideo | null> {
  if (Platform.OS === 'web') {
    return pickProductVideoWebInput();
  }
  return pickProductVideoNative();
}
